import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { useModuleAccess } from '@/hooks/useModuleAccess';
import { AccessDenied } from '@/components/AccessDenied';

interface AdminGuardProps {
  children: React.ReactNode;
}

/**
 * AdminGuard Component
 * 
 * Protects admin routes and UI sections.
 * Allows access if:
 * - user.role === 'super_admin'
 * - OR workspaceRole === 'owner' (via isOwner from useModuleAccess)
 * - OR workspaceRole === 'admin'
 * 
 * Rules:
 * - All hooks called unconditionally (Rules of Hooks)
 * - No early returns before hooks
 * - Reusable for routes and UI sections
 */
export default function AdminGuard({ children }: AdminGuardProps) {
  // ============================================
  // HOOKS - MUST BE CALLED UNCONDITIONALLY
  // ============================================
  const { user } = useAuth();
  const { currentWorkspace } = useWorkspace();
  const moduleAccess = useModuleAccess('workspace', currentWorkspace?.id);
  const navigate = useNavigate();

  // ============================================
  // ACCESS CONTROL LOGIC - AFTER HOOKS
  // ============================================
  
  // Check if user is super admin
  const isSuperAdmin = user?.role === 'super_admin';
  
  // Check if user is workspace owner (via moduleAccess)
  const isOwner = moduleAccess.isOwner;
  
  // Check if user is workspace admin
  // Note: In a full implementation, workspaceRole should come from workspace_members table
  // For now, we check user.role and moduleAccess.userRole
  // TODO: Fetch workspace-specific role from GET /workspaces/:id/members/me when API is available
  const workspaceRole = moduleAccess.userRole; // Currently returns user.role, not workspace-specific role
  const isWorkspaceAdmin = 
    workspaceRole === 'admin' || 
    workspaceRole === 'workspace_admin' ||
    user?.role === 'workspace_admin' || 
    user?.role === 'admin';

  // Determine if user has admin access
  const hasAdminAccess = isSuperAdmin || isOwner || isWorkspaceAdmin;

  // ============================================
  // REDIRECT LOGIC - AFTER HOOKS
  // ============================================
  
  useEffect(() => {
    if (!hasAdminAccess && currentWorkspace?.slug) {
      // Redirect non-admins to dashboard
      navigate(`/${currentWorkspace.slug}/dashboard`, { replace: true });
    }
  }, [hasAdminAccess, currentWorkspace?.slug, navigate]);

  // ============================================
  // RENDER LOGIC
  // ============================================
  
  if (!hasAdminAccess) {
    // Show loading/redirect message while redirecting
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center space-y-2">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
          <p className="text-sm text-muted-foreground">Redirecting to dashboard...</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}

